/**
 * Admin — Site health & SEO radar.
 * Aggregates internal checks (connectivity, sitemap, robots, broken links,
 * Core Web Vitals via PSI) and Google Search Console data (via connector)
 * into a single monitoring dashboard.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Activity, RadioTower, Gauge, Link2, AlertTriangle, CheckCircle2, XCircle,
  Search, RefreshCw, Loader2, Globe, MapPinned, ShieldCheck, Zap, Eye,
  KeyRound, Save, Trash2, EyeOff,
} from "lucide-react";

import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  checkConnectivity,
  checkSitemap,
  scanBrokenLinks,
  pageSpeedInsights,
  gscOverview,
} from "@/lib/admin-monitor.functions";

export const Route = createFileRoute("/admin/monitor")({
  head: () => ({
    meta: [
      { title: "رادار مراقبة الموقع — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MonitorPage,
});

// ---------- small UI atoms ----------

function StatusDot({ ok, tone }: { ok: boolean | null; tone?: "good" | "warn" | "bad" | "muted" }) {
  const resolvedTone = tone ?? (ok === null ? "muted" : ok ? "good" : "bad");
  if (resolvedTone === "muted") return <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground/40" />;
  const toneClass =
    resolvedTone === "good"
      ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.7)]"
      : resolvedTone === "warn"
        ? "bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.7)]"
        : "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.7)]";
  return (
    <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${toneClass}`} />
  );
}


function ScoreRing({ score, label }: { score: number | null; label: string }) {
  const pct = score === null ? 0 : Math.round(score * 100);
  const color =
    score === null ? "stroke-muted-foreground/40" : pct >= 90 ? "stroke-emerald-500" : pct >= 50 ? "stroke-amber-500" : "stroke-red-500";
  const textColor =
    score === null ? "text-muted-foreground" : pct >= 90 ? "text-emerald-500" : pct >= 50 ? "text-amber-500" : "text-red-500";
  const C = 2 * Math.PI * 42;
  const offset = C - (C * pct) / 100;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-20 w-20 sm:h-24 sm:w-24">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" strokeWidth="8" className="stroke-muted/40" fill="none" />
          <circle
            cx="50" cy="50" r="42" strokeWidth="8" fill="none"
            className={`${color} transition-all duration-700`}
            strokeDasharray={C}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center font-bold text-lg ${textColor}`}>
          {score === null ? "—" : pct}
        </div>
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  title,
  value,
  hint,
  tone = "default",
}: {
  icon: typeof Activity;
  title: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const border =
    tone === "good"
      ? "border-emerald-500/40"
      : tone === "warn"
        ? "border-amber-500/40"
        : tone === "bad"
          ? "border-red-500/40"
          : "border-border";
  return (
    <div className={`min-w-0 rounded-2xl border ${border} bg-card/60 backdrop-blur p-4 flex items-start gap-3`}>
      <div className="shrink-0 rounded-xl bg-primary/10 text-primary p-2"><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="text-xl font-bold truncate">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground truncate">{hint}</div>}
      </div>
    </div>
  );
}

function Panel({
  icon: Icon,
  title,
  right,
  children,
}: {
  icon: typeof Activity;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card/60 backdrop-blur">
      <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-primary" />
          <h2 className="min-w-0 truncate text-sm font-bold">{title}</h2>
        </div>
        <div className="shrink-0">{right}</div>
      </header>
      <div className="min-w-0 p-3 sm:p-4">{children}</div>
    </section>
  );
}

// ---------- page ----------

type Conn = Awaited<ReturnType<typeof checkConnectivity>>;
type Sitemap = Awaited<ReturnType<typeof checkSitemap>>;
type Broken = Awaited<ReturnType<typeof scanBrokenLinks>>;
type PSI = Awaited<ReturnType<typeof pageSpeedInsights>>;
type GSC = Awaited<ReturnType<typeof gscOverview>>;

function MonitorPage() {
  const { user, isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();

  const [conn, setConn] = useState<Conn | null>(null);
  const [sitemap, setSitemap] = useState<Sitemap | null>(null);
  const [broken, setBroken] = useState<Broken | null>(null);
  const [psi, setPsi] = useState<PSI | null>(null);
  const [gsc, setGsc] = useState<GSC | null>(null);

  const [loading, setLoading] = useState({ conn: false, sm: false, links: false, psi: false, gsc: false });
  const [strategy, setStrategy] = useState<"mobile" | "desktop">("mobile");
  const [inspectUrl, setInspectUrl] = useState("");

  // Manual credentials — stored ONLY in localStorage on the user's browser.
  // Nothing is persisted server-side. Values are sent to server functions per
  // request over the same-origin HTTPS call and never logged.
  const CREDS_KEY = "rawan.monitor.creds.v2";
  type Creds = {
    psiKey: string;
    gscToken: string;
    gscSite: string;
    gscClientId: string;
    gscClientSecret: string;
    gscRefreshToken: string;
  };
  const emptyCreds: Creds = {
    psiKey: "",
    gscToken: "",
    gscSite: "",
    gscClientId: "",
    gscClientSecret: "",
    gscRefreshToken: "",
  };

  const [creds, setCreds] = useState<Creds>(emptyCreds);
  const [credsDraft, setCredsDraft] = useState<Creds>(emptyCreds);
  const [showSecrets, setShowSecrets] = useState(false);
  const [credsSaved, setCredsSaved] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(CREDS_KEY);
      if (raw) {
        const parsed = { ...emptyCreds, ...(JSON.parse(raw) as Partial<Creds>) };
        setCreds(parsed);
        setCredsDraft(parsed);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveCreds() {
    setCreds(credsDraft);
    try { window.localStorage.setItem(CREDS_KEY, JSON.stringify(credsDraft)); } catch { /* ignore */ }
    setCredsSaved(true);
    setTimeout(() => setCredsSaved(false), 1500);
  }
  function clearCreds() {
    setCreds(emptyCreds);
    setCredsDraft(emptyCreds);
    try { window.localStorage.removeItem(CREDS_KEY); } catch { /* ignore */ }
  }

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate({ to: "/login" });
      return;
    }
    // Auto-run internal checks on first load
    void runConnectivity();
    void runSitemap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin]);

  async function runConnectivity() {
    setLoading((s) => ({ ...s, conn: true }));
    try { setConn(await checkConnectivity()); } finally { setLoading((s) => ({ ...s, conn: false })); }
  }
  async function runSitemap() {
    setLoading((s) => ({ ...s, sm: true }));
    try { setSitemap(await checkSitemap()); } finally { setLoading((s) => ({ ...s, sm: false })); }
  }
  async function runBroken() {
    setLoading((s) => ({ ...s, links: true }));
    try { setBroken(await scanBrokenLinks({ data: { sampleSize: 15 } })); } finally { setLoading((s) => ({ ...s, links: false })); }
  }
  async function runPsi() {
    setLoading((s) => ({ ...s, psi: true }));
    try {
      setPsi(await pageSpeedInsights({ data: { strategy, apiKey: creds.psiKey || undefined } }));
    } finally { setLoading((s) => ({ ...s, psi: false })); }
  }
  async function runGsc() {
    setLoading((s) => ({ ...s, gsc: true }));
    try {
      setGsc(await gscOverview({ data: {
        inspectUrl: inspectUrl || undefined,
        accessToken: creds.gscToken || undefined,
        siteUrl: creds.gscSite || undefined,
        clientId: creds.gscClientId || undefined,
        clientSecret: creds.gscClientSecret || undefined,
        refreshToken: creds.gscRefreshToken || undefined,
      } }));
    } finally { setLoading((s) => ({ ...s, gsc: false })); }
  }

  async function runAll() {
    await Promise.all([runConnectivity(), runSitemap(), runBroken(), runPsi(), runGsc()]);
  }


  if (authLoading || !isAdmin) return null;

  const healthScore = (() => {
    const parts: number[] = [];
    if (conn) parts.push(conn.reachable ? 100 : 0);
    if (sitemap) parts.push(sitemap.reachable && sitemap.wellFormed ? 100 : 40);
    if (broken) parts.push(broken.checked ? Math.round((broken.ok / broken.checked) * 100) : 0);
    if (psi && psi.ok && psi.scores?.performance != null) parts.push(Math.round((psi.scores.performance ?? 0) * 100));
    if (parts.length === 0) return null;
    return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
  })();

  return (
    <div className="min-h-screen max-w-full overflow-x-hidden bg-background text-foreground">
      <Header />
      <main className="container mx-auto max-w-full overflow-x-hidden px-3 py-6 sm:px-4 space-y-6" dir="rtl">
        <BackToDashboard />

        {/* Radar header */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-transparent to-transparent p-6">
          <div className="absolute -top-32 -end-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-2 border-primary/50 grid place-items-center animate-pulse">
                  <RadioTower className="h-7 w-7 text-primary" />
                </div>
                <span className="absolute -inset-2 rounded-full border border-primary/20" />
              </div>
              <div>
                <h1 className="text-2xl font-black">رادار مراقبة الموقع</h1>
                <p className="text-sm text-muted-foreground">
                  الأداء • SEO • الأرشفة • الروابط المكسورة • الاتصال
                </p>
              </div>
            </div>
            <div className="md:ms-auto flex items-center gap-3">
              <div className="text-center">
                <div className="text-4xl font-black leading-none">
                  {healthScore === null ? "—" : healthScore}
                  <span className="text-base font-normal text-muted-foreground">/100</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">مؤشر الجودة الكلي</div>
              </div>
              <Button onClick={runAll} className="gap-2">
                <RefreshCw className="h-4 w-4" /> فحص شامل
              </Button>
            </div>
          </div>
        </div>

        {/* Manual credentials — browser-local only */}
        <Panel
          icon={KeyRound}
          title="مفاتيح المراقبة (تُحفظ في متصفحك فقط)"
          right={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowSecrets((v) => !v)}>
                {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              {(creds.psiKey || creds.gscToken || creds.gscSite || creds.gscClientId || creds.gscClientSecret || creds.gscRefreshToken) && (
                <Button size="sm" variant="ghost" onClick={clearCreds} title="مسح">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
              <Button size="sm" onClick={saveCreds} className="gap-1">
                <Save className="h-4 w-4" /> {credsSaved ? "تم الحفظ" : "حفظ"}
              </Button>
            </div>
          }
        >
          <div className="grid md:grid-cols-2 gap-3 text-xs">

            <div className="space-y-1">
              <label className="font-bold flex items-center gap-2">
                <Zap className="h-3.5 w-3.5" /> PageSpeed API Key
                {creds.psiKey && <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">نشط</Badge>}
              </label>
              <Input
                type={showSecrets ? "text" : "password"}
                value={credsDraft.psiKey}
                onChange={(e) => setCredsDraft((c) => ({ ...c, psiKey: e.target.value }))}
                placeholder="اختياري — يستخدم المفتاح الافتراضي إن تُرك فارغاً"
                className="h-8 font-mono"
                autoComplete="off"
              />
              <p className="text-muted-foreground">
                يتجاوز مفتاح الخادم. احصل عليه من{" "}
                <a href="https://developers.google.com/speed/docs/insights/v5/get-started" target="_blank" rel="noreferrer" className="underline">Google Cloud Console</a>.
              </p>
            </div>
            <div className="space-y-1">
              <label className="font-bold flex items-center gap-2">
                <Globe className="h-3.5 w-3.5" /> عنوان موقع GSC
              </label>
              <Input
                value={credsDraft.gscSite}
                onChange={(e) => setCredsDraft((c) => ({ ...c, gscSite: e.target.value }))}
                placeholder="sc-domain:example.com أو https://example.com/"
                className="h-8 font-mono"
                autoComplete="off"
              />
              <p className="text-muted-foreground">
                اتركه فارغاً لاختيار أول موقع متاح تلقائياً.
              </p>
            </div>
          </div>

          {/* Refresh-token flow — dائم، لا ينتهي */}
          <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              <span className="font-bold text-xs">GSC — تدفق دائم عبر Refresh Token (موصى به)</span>
              {(creds.gscClientId && creds.gscClientSecret && creds.gscRefreshToken) && (
                <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">مفعّل — لا ينتهي</Badge>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-xs">
              <div className="space-y-1">
                <label className="font-bold">Client ID</label>
                <Input
                  type={showSecrets ? "text" : "password"}
                  value={credsDraft.gscClientId}
                  onChange={(e) => setCredsDraft((c) => ({ ...c, gscClientId: e.target.value }))}
                  placeholder="xxxx.apps.googleusercontent.com"
                  className="h-8 font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold">Client Secret</label>
                <Input
                  type={showSecrets ? "text" : "password"}
                  value={credsDraft.gscClientSecret}
                  onChange={(e) => setCredsDraft((c) => ({ ...c, gscClientSecret: e.target.value }))}
                  placeholder="GOCSPX-..."
                  className="h-8 font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold">Refresh Token</label>
                <Input
                  type={showSecrets ? "text" : "password"}
                  value={credsDraft.gscRefreshToken}
                  onChange={(e) => setCredsDraft((c) => ({ ...c, gscRefreshToken: e.target.value }))}
                  placeholder="1//0g..."
                  className="h-8 font-mono"
                  autoComplete="off"
                />
              </div>
            </div>
            <details className="mt-2 text-[11px] text-muted-foreground">
              <summary className="cursor-pointer font-bold text-foreground">كيف أحصل عليها؟ (مرة واحدة، دائمة)</summary>
              <ol className="list-decimal ps-5 mt-2 space-y-1">
                <li>افتح <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline">Google Cloud Console → Credentials</a> وأنشئ <b>OAuth Client ID</b> نوع <b>Web application</b>.</li>
                <li>أضف <code>https://developers.google.com/oauthplayground</code> ضمن <b>Authorized redirect URIs</b>.</li>
                <li>فعّل <b>Google Search Console API</b> في مشروعك.</li>
                <li>افتح <a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noreferrer" className="underline">OAuth Playground</a> → ⚙️ (Settings) → فعّل <b>Use your own OAuth credentials</b> وألصق Client ID/Secret.</li>
                <li>اختر النطاق <code>https://www.googleapis.com/auth/webmasters.readonly</code> ثم <b>Authorize APIs</b> ووافق.</li>
                <li>اضغط <b>Exchange authorization code for tokens</b> — انسخ <b>refresh_token</b>.</li>
                <li>ألصق الثلاثة هنا واحفظ. الخادم سيجدّد access_token تلقائياً عند كل طلب.</li>
              </ol>
              <p className="mt-2">⚠️ refresh_token لا ينتهي إلا إذا أبطلته يدوياً من حسابك أو لم يُستخدم 6 أشهر.</p>
            </details>
          </div>

          {/* Short-lived access token — fallback for quick testing */}
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer font-bold text-muted-foreground flex items-center gap-2">
              <Search className="h-3.5 w-3.5" /> بديل مؤقت: Access Token يدوي (ساعة فقط)
              {creds.gscToken && <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">نشط</Badge>}
            </summary>
            <div className="mt-2 space-y-1">
              <Input
                type={showSecrets ? "text" : "password"}
                value={credsDraft.gscToken}
                onChange={(e) => setCredsDraft((c) => ({ ...c, gscToken: e.target.value }))}
                placeholder="ya29...."
                className="h-8 font-mono"
                autoComplete="off"
              />
              <p className="text-muted-foreground">
                يُستخدم فقط عند غياب Refresh Token. للاختبار السريع من OAuth Playground.
              </p>
            </div>
          </details>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-muted-foreground border-t border-border pt-3">

            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
            <span>
              القيم تُحفظ في <code>localStorage</code> على جهازك فقط، وتُرسل عبر HTTPS إلى دوال الخادم عند كل طلب دون تسجيلها أو تخزينها.
            </span>
          </div>
        </Panel>

        {/* Top stat strip */}
        <div className="grid min-w-0 grid-cols-2 md:grid-cols-4 gap-3">

          <StatCard
            icon={Globe}
            title="الاتصال"
            value={conn ? (conn.reachable ? "متصل" : "معطّل") : loading.conn ? "…" : "—"}
            hint={conn ? `${conn.status} • ${conn.responseMs}ms` : conn === null ? "لم يُفحص" : ""}
            tone={conn ? (conn.reachable ? "good" : "bad") : "default"}
          />
          <StatCard
            icon={MapPinned}
            title="Sitemap"
            value={sitemap ? `${sitemap.urlCount} رابط` : "—"}
            hint={sitemap ? (sitemap.wellFormed ? "صالح XML" : "غير صالح") : ""}
            tone={sitemap ? (sitemap.reachable && sitemap.wellFormed ? "good" : "warn") : "default"}
          />
          <StatCard
            icon={Link2}
            title="روابط مكسورة"
            value={broken ? `${broken.broken}/${broken.checked}` : "—"}
            hint={broken ? `متوسط ${broken.avgMs}ms` : "اضغط لفحص عينة"}
            tone={broken ? (broken.broken === 0 ? "good" : broken.broken < 3 ? "warn" : "bad") : "default"}
          />
          <StatCard
            icon={Zap}
            title="الأداء (PSI)"
            value={psi && psi.ok ? `${Math.round((psi.scores.performance ?? 0) * 100)}` : "—"}
            hint={psi && psi.ok ? `LCP ${psi.vitals.lcp} • CLS ${psi.vitals.cls}` : psi && !psi.ok ? "غير متاح" : ""}
            tone={psi && psi.ok ? ((psi.scores.performance ?? 0) >= 0.9 ? "good" : (psi.scores.performance ?? 0) >= 0.5 ? "warn" : "bad") : "default"}
          />
        </div>

        {/* Panels grid */}
        <div className="grid min-w-0 lg:grid-cols-2 gap-6">
          {/* Connectivity + SEO basics */}
          <Panel
            icon={ShieldCheck}
            title="الاتصال وصحة الميتا"
            right={
              <Button size="sm" variant="ghost" onClick={runConnectivity} disabled={loading.conn}>
                {loading.conn ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            }
          >
            {!conn ? (
              <div className="text-sm text-muted-foreground">لم يُفحص بعد.</div>
            ) : (
              <div className="space-y-3 text-sm">
                {!conn.reachable && conn.reason && (
                  <div className="rounded-md border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-500 break-words">
                    سبب فشل الفحص: {conn.reason}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2"><StatusDot ok={conn.reachable} /> <span className="shrink-0">النطاق:</span> <code className="text-xs break-all min-w-0">{conn.origin}</code></div>
                <div className="flex flex-wrap items-center gap-2"><StatusDot ok={conn.responseMs < 800} /> <span className="shrink-0">زمن الاستجابة:</span> <b>{conn.responseMs}ms</b> (HTTP {conn.status})</div>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="flex items-center gap-2"><StatusDot ok={conn.seo.hasTitle} /> Title</div>
                  <div className="flex items-center gap-2"><StatusDot ok={conn.seo.hasDescription} /> Description</div>
                  <div className="flex items-center gap-2"><StatusDot ok={conn.seo.hasCanonical} /> Canonical</div>
                  <div className="flex items-center gap-2"><StatusDot ok={conn.seo.hasOg} /> Open Graph</div>
                </div>
                {conn.seo.title && (
                  <div className="text-xs text-muted-foreground border-t border-border pt-2 break-words">
                    عنوان الصفحة الرئيسية: <span className="text-foreground">{conn.seo.title}</span>
                  </div>
                )}

              </div>
            )}
          </Panel>

          {/* Sitemap health */}
          <Panel
            icon={MapPinned}
            title="صحة Sitemap و robots.txt"
            right={
              <Button size="sm" variant="ghost" onClick={runSitemap} disabled={loading.sm}>
                {loading.sm ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            }
          >
            {!sitemap ? (
              <div className="text-sm text-muted-foreground">لم يُفحص بعد.</div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><StatusDot ok={sitemap.reachable} /> /sitemap.xml — HTTP {sitemap.status}</div>
                <div className="flex items-center gap-2"><StatusDot ok={sitemap.wellFormed} /> XML صالح ({(sitemap.xmlBytes / 1024).toFixed(1)} KB)</div>
                <div className="flex items-center gap-2"><StatusDot ok={sitemap.urlCount > 0} /> {sitemap.urlCount} رابط في السايتماب</div>
                <div className="flex items-center gap-2"><StatusDot ok={Boolean(sitemap.lastmodLatest)} /> آخر تحديث: {sitemap.lastmodLatest ?? "—"}</div>
                <div className="border-t border-border mt-3 pt-3 space-y-2">
                  <div className="flex items-center gap-2"><StatusDot ok={sitemap.robots.exists} /> /robots.txt موجود</div>
                  <div className="flex items-center gap-2"><StatusDot ok={sitemap.robots.allowsAll} /> يسمح للزحف (Allow: /)</div>
                  <div className="flex items-center gap-2"><StatusDot ok={sitemap.robots.hasSitemapDirective} /> يشير إلى Sitemap</div>
                </div>
              </div>
            )}
          </Panel>

          {/* Broken links */}
          <Panel
            icon={Link2}
            title="فحص الروابط المكسورة (عيّنة عشوائية)"
            right={
              <Button size="sm" variant="outline" onClick={runBroken} disabled={loading.links}>
                {loading.links ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
                <span className="ms-1">فحص 15 رابط</span>
              </Button>
            }
          >
            {!broken ? (
              <div className="text-sm text-muted-foreground">لم يُفحص بعد. اختيار عشوائي من السايتماب مع تجنّب استهلاك النطاق.</div>
            ) : broken.error ? (
              <div className="text-sm text-red-500">{broken.error}</div>
            ) : (
              <div className="space-y-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                  <Badge variant="secondary">فُحص {broken.checked}</Badge>
                  <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">سليم {broken.ok}</Badge>
                  <Badge className="bg-red-500/15 text-red-500 border-red-500/30">مكسور {broken.broken}</Badge>
                  <span className="text-xs text-muted-foreground sm:ms-auto">متوسط {broken.avgMs}ms</span>
                </div>
                <ul className="max-h-64 max-w-full overflow-y-auto overflow-x-hidden divide-y divide-border rounded-xl border border-border">
                  {broken.sample.map((r) => (
                    <li key={r.url} dir="ltr" className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1 px-3 py-2 text-xs">
                      {r.ok ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      <span className="min-w-0 break-all text-left font-mono leading-relaxed">{r.url}</span>
                      <span className={r.ok ? "shrink-0 text-emerald-500" : "shrink-0 text-red-500"}>{r.status || "ERR"}</span>
                      <span className="col-start-2 text-left text-muted-foreground">{r.ms}ms</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Panel>

          {/* PageSpeed / Core Web Vitals */}
          <Panel
            icon={Gauge}
            title="Core Web Vitals — PageSpeed Insights"
            right={
              <div className="flex items-center gap-2">
                <select
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value as "mobile" | "desktop")}
                  className="text-xs rounded-md border border-border bg-background px-2 py-1"
                >
                  <option value="mobile">جوّال</option>
                  <option value="desktop">حاسوب</option>
                </select>
                <Button size="sm" variant="outline" onClick={runPsi} disabled={loading.psi}>
                  {loading.psi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  <span className="ms-1">تشغيل</span>
                </Button>
              </div>
            }
          >
            {!psi ? (
              <div className="text-sm text-muted-foreground">اضغط «تشغيل» لبدء فحص PageSpeed (قد يستغرق 15–30 ثانية).</div>
            ) : !psi.ok ? (
              <div className="text-sm text-red-500 break-all">{psi.error}</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <ScoreRing score={psi.scores.performance} label="أداء" />
                  <ScoreRing score={psi.scores.seo} label="SEO" />
                  <ScoreRing score={psi.scores.accessibility} label="وصول" />
                  <ScoreRing score={psi.scores.bestPractices} label="ممارسات" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {Object.entries(psi.vitals).map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                      <div className="font-bold">{String(v ?? "—")}</div>
                    </div>
                  ))}
                </div>
                {!psi.hasKey && (
                  <div className="text-xs text-amber-500 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                    أضف <code>GOOGLE_API_KEY</code> في لوحة المفاتيح أعلاه أو في أسرار المشروع لرفع الحصة اليومية.
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* GSC */}
          <Panel
            icon={Search}
            title="Google Search Console — الأرشفة والأداء البحثي"
            right={
              <Button size="sm" variant="outline" onClick={runGsc} disabled={loading.gsc}>
                {loading.gsc ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ms-1">تحديث</span>
              </Button>
            }
          >
            {!gsc ? (
              <div className="text-sm text-muted-foreground">اضغط «تحديث» لجلب بيانات آخر 28 يوماً.</div>
            ) : !gsc.connected ? (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                <div className="flex items-center gap-2 font-bold mb-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> GSC غير مرتبط
                </div>
                <p className="text-xs text-muted-foreground">{gsc.error}</p>
                <p className="text-xs mt-2">أضف <b>Client ID + Secret + Refresh Token</b> من لوحة المفاتيح أعلاه (اتصال دائم).</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground break-all">
                  المواقع المُتحقّق منها: <b>{gsc.sites?.length ?? 0}</b> — العرض لـ{" "}
                  <code className="text-foreground break-all">{gsc.chosenSite ?? "—"}</code>
                </div>
                {gsc.errors?.sites && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-600 dark:text-amber-400 break-words">
                    {gsc.errors.sites}
                  </div>
                )}

                {gsc.analytics?.totals && (
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="rounded-lg border border-border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">النقرات</div>
                      <div className="font-bold">{gsc.analytics.totals.clicks.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">الظهور</div>
                      <div className="font-bold">{gsc.analytics.totals.impressions.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">CTR</div>
                      <div className="font-bold">{(gsc.analytics.totals.ctr * 100).toFixed(2)}%</div>
                    </div>
                    <div className="rounded-lg border border-border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">الموضع</div>
                      <div className="font-bold">{gsc.analytics.totals.position.toFixed(1)}</div>
                    </div>
                  </div>
                )}
                {gsc.sitemaps && gsc.sitemaps.length > 0 && (
                  <div>
                    <div className="text-xs font-bold mb-1">Sitemaps المُقدَّمة</div>
                    <ul className="max-w-full overflow-hidden text-xs divide-y divide-border rounded-lg border border-border">
                      {gsc.sitemaps.map((s, i) => {
                        const errors = Number(s.errors ?? 0) || 0;
                        const warnings = Number(s.warnings ?? 0) || 0;
                        const isPending = s.isPending === true || String(s.isPending).toLowerCase() === "true";
                        const tone = errors > 0 ? "bad" : isPending || warnings > 0 ? "warn" : "good";
                        const statusText = errors > 0 ? `أخطاء ${errors}` : isPending ? "قيد المعالجة" : warnings > 0 ? `تحذيرات ${warnings}` : "سليم";
                        return (
                          <li key={i} className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-2 py-1.5">
                            <StatusDot ok={null} tone={tone} />
                            <span dir="ltr" className="min-w-0 break-all text-left font-mono leading-relaxed" title={s.path}>{s.path}</span>
                            <span className={tone === "good" ? "shrink-0 text-emerald-500" : tone === "warn" ? "shrink-0 text-amber-500" : "shrink-0 text-red-500"}>{statusText}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {gsc.analytics?.topQueries && gsc.analytics.topQueries.length > 0 && (
                  <div>
                    <div className="text-xs font-bold mb-1">أعلى الكلمات المفتاحية</div>
                    <ul className="text-xs divide-y divide-border rounded-lg border border-border max-h-48 overflow-auto">
                      {gsc.analytics.topQueries.slice(0, 8).map((q, i) => (
                        <li key={i} className="flex items-center gap-2 px-2 py-1.5">
                          <span className="truncate flex-1">{q.keys?.[0]}</span>
                          <span className="text-muted-foreground">{q.clicks ?? 0} نقرة</span>
                          <span className="text-muted-foreground">#{(q.position ?? 0).toFixed(1)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="border-t border-border pt-3">
                  <div className="text-xs font-bold mb-2 flex items-center gap-2"><Eye className="h-3.5 w-3.5" /> فحص URL معيّن</div>
                  <div className="flex gap-2">
                    <Input
                      value={inspectUrl}
                      onChange={(e) => setInspectUrl(e.target.value)}
                      placeholder="https://example.com/page"
                      className="h-8 text-xs"
                    />
                    <Button size="sm" onClick={runGsc} disabled={loading.gsc}>فحص</Button>
                  </div>
                  {gsc.inspection?.inspectionResult?.indexStatusResult && (
                    <div className="mt-2 text-xs rounded-lg border border-border p-2 space-y-1">
                      <div>الحكم: <b>{gsc.inspection.inspectionResult.indexStatusResult.verdict ?? "—"}</b></div>
                      <div>التغطية: {gsc.inspection.inspectionResult.indexStatusResult.coverageState ?? "—"}</div>
                      <div>الأرشفة: {gsc.inspection.inspectionResult.indexStatusResult.indexingState ?? "—"}</div>
                      <div>آخر زحف: {gsc.inspection.inspectionResult.indexStatusResult.lastCrawlTime ?? "—"}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Panel>
        </div>
      </main>
      <Footer />
    </div>
  );
}
